import { HarmonyTheme } from '@uhg-harmony/foundations';
import {
  horizonOptumTheme,
  horizonRallyTheme,
  horizonUhcTheme,
  darkTheme,
} from 'builder/scenes/SiteBuilder/SiteStylesPanel/BrandThemes';
import { ThemeType } from 'shared/util/ThemeType';

const isLegacyTheme = (t: any) => !!t?.colors && !!t?.colors.primary;

const isHarmonyTheme = (t: any) =>
  t && (t as HarmonyTheme).HarmonyCoreColorBlue115 !== undefined;

const adaptHarmonyThemeToLegacy = (harmony: HarmonyTheme): Theme => ({
  font: {
    size: { body: { base: { value: 11 } } },
    weight: { base: { value: 'normal' } },
    family: { base: { value: 'OptumSans' } },
  },
  colors: {
    primary: '#FF612B',
    secondary: '#002667',
    tertiary: '#D9F6FA',
    quaternary: '#B0E3EA',
    text: {
      primary: harmony?.HarmonyCoreColorNeutral100 || '#333333',
      secondary: harmony?.HarmonyCoreColorNeutral0 || '#ffffff',
    },
    custom: [
      { name: 'Brand 50', id: 'brand50', value: '#FF612B', type: 'Brand' },
      {
        name: 'Primary 100',
        id: 'primary100',
        value: '#002667',
        type: 'Brand',
      },
      { name: 'Primary 10', id: 'primary10', value: '#D9F6FA', type: 'Brand' },
      { name: 'Primary 5', id: 'primary5', value: '#FBF9F4', type: 'Brand' },
    ],
    aux: {
      lightestBlue: '#bac0db',
      lightBlue: '#1595fa',
      blue: '#3150bc',
      darkBlue: '#3477db',
      darkNavyBlue: '#002677',
      darkestBlue: '#09228d',
      black: '#000000',
      darkGrey: '#666666',
      lightGrey: '#D1D5D8',
      grey: '#879196',
      offWhite: '#f8f8f8',
      green: '#297a29',
      lightGreen: '#7ed321',
      lighterGreen: '#e0f5e0',
      yellow: '#f29f00',
      lightYellow: '#fff1d6',
      brightYellow: '#f8e81c',
      pink: '#b704d7',
      lightPink: '#fcecfe',
      red: '#e40005',
      lightRed: '#ff787c',
      errorRed: '#c40000',
      blueGrey: '#f2f6f9',
      white: '#ffffff',
      lighterGrey: '#e4e5e7',
      lightestGrey: '#fafafa',
      mutedGrey: '#2B3133',
      skyBlue: '#d9f6fa',
    },
  },
});

const defaultAux = horizonOptumTheme.colors.aux;

/**
 * withCompleteAux: Merges theme colors with safe defaults, ensuring `aux`,
 * `text`, `font`, and `custom` properties are always present and valid.
 */
function withCompleteAux(theme: Theme): Theme {
  const safeColors = theme.colors || ({} as Theme['colors']);
  const safeAux = { ...defaultAux, ...(safeColors.aux ?? {}) };

  return {
    ...theme,
    font: theme.font ?? horizonOptumTheme.font,
    colors: {
      ...horizonOptumTheme.colors,
      ...safeColors,
      // Prefer safeColors.text; fall back to horizonOptumTheme default to prevent null override
      text: safeColors.text ?? horizonOptumTheme.colors.text,
      custom: safeColors.custom ?? horizonOptumTheme.colors.custom,
      aux: safeAux,
    },
  };
}

const brandByKey: Record<string, Theme> = {
  optum: horizonOptumTheme,
  uhc: horizonUhcTheme,
  rally: horizonRallyTheme,
  dark: darkTheme,
};

/**
 * ensureThemeText: Ensures the theme has a valid `text` color property
 * (`primary` and `secondary`), falling back through custom tokens, dark-mode
 * detection, and hard-coded safe values.
 */
export const ensureThemeText = (theme: Theme): Theme => {
  const colors = theme.colors ?? ({} as Theme['colors']);
  const custom = colors.custom ?? [];

  // try to pick from custom tokens if present (e.g., dark theme file)
  const customPrimary = custom.find(c => c.id === 'textPrimary')?.value;
  const customSecondary = custom.find(c => c.id === 'textSecondary')?.value;

  // naive dark check (tweak if you have a better flag)
  const isDark =
    colors.quaternary === '#121212' ||
    custom.some(c => c.id === 'pageBackground' && c.value === '#121212');

  const text = {
    primary:
      colors.text?.primary ?? customPrimary ?? (isDark ? '#FFFFFF' : '#000000'),
    secondary:
      colors.text?.secondary ??
      customSecondary ??
      (isDark ? '#B0BEC5' : '#666666'),
  };

  return {
    ...theme,
    colors: {
      ...colors,
      text,
    },
  };
};

/**
 * Always returns a complete legacy Theme:
 * - undefined -> Optum default
 * - Harmony bundle -> adapted
 * - Legacy theme -> normalized (ensures aux)
 * - Brand key / customThemeX overlays -> merged with base
 */
export function resolveLegacyThemeFromSite(siteTheme?: any): Theme {
  // Guard: missing or non-object theme — return safe default instead of crashing.
  if (!siteTheme || typeof siteTheme !== 'object') {
    if (siteTheme !== undefined && siteTheme !== null) {
      console.warn(
        'resolveLegacyThemeFromSite: siteTheme is malformed, using safe default.',
        siteTheme
      );
    }
    return withCompleteAux(horizonOptumTheme);
  }

  if (isLegacyTheme(siteTheme))
    return ensureThemeText(withCompleteAux(siteTheme as Theme));

  if (isHarmonyTheme(siteTheme)) {
    return withCompleteAux(
      adaptHarmonyThemeToLegacy(siteTheme as HarmonyTheme)
    );
  }

  const enabled = String(siteTheme?.enabledTheme || 'optum');
  let base: Theme = brandByKey[enabled] || horizonOptumTheme;

  // support customThemeN (any number)
  const customMatch = /^customTheme(\d+)$/.exec(enabled);
  if (customMatch && siteTheme?.[enabled]?.colors) {
    const c = siteTheme[enabled].colors;
    base = {
      ...base,
      colors: {
        ...base.colors,
        primary: c.primary ?? base.colors.primary,
        secondary: c.secondary ?? base.colors.secondary,
        tertiary: c.tertiary ?? base.colors.tertiary,
        quaternary: c.quaternary ?? base.colors.quaternary,
        custom: c.custom ?? base.colors.custom,
        aux: base.colors.aux,
      },
    };
  }

  if (siteTheme?.colors) {
    const sc = siteTheme.colors;
    base = {
      ...base,
      colors: {
        ...base.colors,
        ...sc,
        custom: sc.custom ?? base.colors.custom,
        aux: { ...base.colors.aux, ...(sc.aux ?? {}) },
      },
    };
  }
  if (siteTheme?.font) {
    base = {
      ...base,
      font: {
        ...base.font,
        ...siteTheme.font,
      },
    };
  }

  return ensureThemeText(withCompleteAux(base));
}

/**
 * Returns an array of available themes for the given site.
 * Includes brand themes and any defined custom themes.
 * Logs an error and returns an empty array if `site` or `site.theme` is malformed.
 *
 * @param site - The current site containing theme configuration.
 * @returns Array of theme descriptors with `key`, `name`, optional `type`, and `theme`.
 */
export const getAvailableThemes = (
  site: Site
): Array<{
  key: string;
  name: string;
  type?: ThemeType;
  theme: Theme;
}> => {
  if (!site || !site.theme) {
    console.error('getAvailableThemes: site or site.theme is undefined', site);
    return [];
  }
  const themes: Array<{
    key: string;
    name: string;
    type?: ThemeType;
    theme: Theme;
  }> = [
    {
      key: 'optum',
      name: 'Optum',
      type: ThemeType.Optum,
      theme: ensureThemeText(horizonOptumTheme),
    },
    {
      key: 'uhc',
      name: 'UHC',
      type: ThemeType.Uhc,
      theme: ensureThemeText(horizonUhcTheme),
    },
    {
      key: 'rally',
      name: 'Rally',
      type: ThemeType.Rally,
      theme: ensureThemeText(horizonRallyTheme),
    },
    {
      key: 'dark',
      name: 'Dark',
      type: ThemeType.Dark,
      theme: ensureThemeText(darkTheme),
    },
  ];

  for (let i = 1; i <= 3; i++) {
    const key = `customTheme${i}`;
    const customTheme = (site.theme as any)[key];
    if (customTheme) {
      themes.push({
        key,
        name: customTheme.name ?? `Custom Theme ${i}`,
        theme: ensureThemeText(customTheme),
      });
    }
  }

  return themes;
};
